import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { path: '/', labelKey: 'nav.home' },
  { path: '/nosotros', labelKey: 'nav.about' },
  { path: '/programas', labelKey: 'nav.programs' },
  { path: '/admisiones', labelKey: 'nav.admissions' },
  { path: '/contacto', labelKey: 'nav.contact' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const changeLanguage = useCallback((lng: string) => {
    i18n.changeLanguage(lng);
  }, [i18n]);

  const isActive = (path: string) => location.pathname === path;

  const getNavLinkClasses = (path: string) => {
    const active = isActive(path);
    if (scrolled) {
      return active
        ? 'text-purple-800 bg-purple-50/80'
        : 'text-purple-700 hover:text-purple-900 hover:bg-purple-50/60';
    }
    return active
      ? 'text-yellow-400 bg-white/15'
      : 'text-white/90 hover:text-white hover:bg-white/10';
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-white/95 backdrop-blur-md py-2.5 shadow-sm' : 'bg-transparent py-4'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-16">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img
              src="https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/64095c3d-7a3b-4158-984f-cbf732794666_Prueba.jpg?v=17a98cf8306df21a873dae6ce81de0d3"
              alt="San Lorenzo School Logo"
              className="w-12 h-12 md:w-14 md:h-14 rounded-lg object-contain bg-white shadow-sm"
            />
            <div className="hidden sm:block">
              <h1
                className={`font-serif font-bold text-base md:text-lg leading-tight transition-colors ${
                  scrolled ? 'text-purple-900' : 'text-white'
                }`}
              >
                San Lorenzo
              </h1>
              <p
                className={`text-[10px] tracking-[0.2em] uppercase transition-colors ${
                  scrolled ? 'text-purple-600' : 'text-white/70'
                }`}
              >
                School
              </p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-200 whitespace-nowrap ${getNavLinkClasses(link.path)}`}
              >
                {t(link.labelKey)}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Language switcher */}
            <div className={`hidden sm:flex items-center rounded-lg overflow-hidden border transition-colors ${scrolled ? 'border-purple-200' : 'border-white/30'}`} role="group" aria-label="Language selector">
              <button
                onClick={() => changeLanguage('es')}
                aria-label="Cambiar a Español"
                aria-pressed={i18n.language === 'es'}
                className={`px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${
                  i18n.language === 'es'
                    ? scrolled ? 'bg-purple-800 text-white' : 'bg-white text-purple-800'
                    : scrolled ? 'text-purple-600 hover:bg-purple-50' : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                ES
              </button>
              <button
                onClick={() => changeLanguage('en')}
                aria-label="Switch to English"
                aria-pressed={i18n.language === 'en'}
                className={`px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${
                  i18n.language === 'en'
                    ? scrolled ? 'bg-purple-800 text-white' : 'bg-white text-purple-800'
                    : scrolled ? 'text-purple-600 hover:bg-purple-50' : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => changeLanguage('fr')}
                aria-label="Passer au Français"
                aria-pressed={i18n.language === 'fr'}
                className={`px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${
                  i18n.language === 'fr'
                    ? scrolled ? 'bg-purple-800 text-white' : 'bg-white text-purple-800'
                    : scrolled ? 'text-purple-600 hover:bg-purple-50' : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
              >
                FR
              </button>
            </div>

            {/* CTA Button - Admissions in yellow */}
            <Link
              to="/admisiones"
              className={`hidden md:inline-flex items-center gap-1.5 px-5 py-2.5 text-[13px] font-semibold rounded-lg transition-all duration-200 whitespace-nowrap ${
                scrolled
                  ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                  : 'bg-yellow-500 text-white hover:bg-yellow-600'
              }`}
            >
              {t('nav.requestInfo')}
              <i className="ri-arrow-right-line text-sm" />
            </Link>

            {/* Mobile toggle */}
            <button
              className="lg:hidden p-2 rounded-lg transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              <i className={`ri-menu-3-line text-xl ${scrolled ? 'text-purple-800' : 'text-white'}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-purple-100 shadow-2xl max-h-[calc(100vh-80px)] overflow-y-auto">
          <div className="px-4 py-5 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-purple-800 bg-purple-50'
                    : 'text-purple-800 hover:bg-purple-50'
                }`}
              >
                {t(link.labelKey)}
              </Link>
            ))}

            {/* Mobile Language */}
            <div className="flex items-center gap-2 px-4 pt-2">
              <button
                onClick={() => changeLanguage('es')}
                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors flex-1 ${
                  i18n.language === 'es' ? 'bg-purple-800 text-white' : 'bg-purple-100 text-purple-700'
                }`}
              >
                Español
              </button>
              <button
                onClick={() => changeLanguage('en')}
                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors flex-1 ${
                  i18n.language === 'en' ? 'bg-purple-800 text-white' : 'bg-purple-100 text-purple-700'
                }`}
              >
                English
              </button>
              <button
                onClick={() => changeLanguage('fr')}
                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors flex-1 ${
                  i18n.language === 'fr' ? 'bg-purple-800 text-white' : 'bg-purple-100 text-purple-700'
                }`}
              >
                Français
              </button>
            </div>

            <Link
              to="/admisiones"
              className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold bg-yellow-500 text-white rounded-lg mt-3 hover:bg-yellow-600 transition-colors"
            >
              {t('nav.requestInfo')}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}